<template>
  <v-menu
    v-if="!readonly"
    ref="menu"
    v-model="menu"
    :content-class="dateTimeMode ? 'w-date-time-field-wrapper' : ''"
    :close-on-content-click="false"
    transition="scale-transition"
    offset-y
    min-width="18.125rem"
    fixed
    @update:return-value="$emit('update:return-value')"
  >
    <template #activator="{ on: { click: openDatePicker } }">
      <w-text-field
        ref="textField"
        class="w-date-field"
        v-bind="$props"
        :value="formattedText"
        :clearable="isClearable"
        append-icon="$wCalendar"
        :error-messages="errorMessages"
        @change="setFormattedDateStringAndParseDateToIso($event)"
        @click:append="openDatePicker"
        @click:clear="$emit('input', null)"
        @blur="$emit('blur', $event)"
      />
    </template>
    <v-date-picker
      ref="picker"
      v-model="dateModel"
      :range="range"
      :class="{ 'v-date-picker__hide-header': hideYear }"
      no-title
      :scrollable="scrollable"
      locale="de"
      :first-day-of-week="1"
      :type="month ? 'month' : undefined"
      :min="min"
      :max="max"
      :allowed-dates="allowedDates"
      :events="events"
      :event-color="eventColor"
      @input="handleDatePickerInput"
      @update:picker-date="$event => $emit('update:picker-date', $event)"
    />

    <w-row class="w-date-time-field__time">
      <v-spacer />
      <w-column v-if="dateTimeMode">
        <label v-if="range">{{ $t('Common.start') }} {{
          $t('ConfigFixtures.words.time') }}</label>
        <WorkTimePicker
          ref="picker"
          v-model="internalTimeValueFrom"
          time-format="HH:mm"
          format="24hr"
          :scrollable="scrollable"
          :disabled="dateModel === undefined"
          locale="de"
        />
        <label v-if="internalTimeValueTo">{{ $t('Common.end') }} {{
          $t('ConfigFixtures.words.time') }}</label>
        <WorkTimePicker
          v-if="internalTimeValueTo"
          ref="picker"
          v-model="internalTimeValueTo"
          time-format="HH:mm"
          format="24hr"
          :scrollable="scrollable"
          :disabled="dateModel === undefined"
          locale="de"
        />
      </w-column>
      <v-spacer />
    </w-row>
  </v-menu>
  <w-read-only-text-field
    v-else
    v-bind="$props"
    :value="formattedText"
  />
</template>

<script>
// components
import WorkTimePicker from './WorkTimePicker';
import { VTextField } from 'vuetify/lib';

// mixins
import isRequired from '@/mixins/component/is-required-mixin';
import pendingAble from '@/mixins/component/pending-able-mixin';
import REGEX from '@/validation/enum/REGEX';

// composables
import { useTimerManager } from '@/composables/timer-manager';

// services
import dateService from '@/services/date-service';
import { DEFAULT_TIME_OF_DAY } from '@/services/temporal-amount-format-service';

export default {
  name: 'WorkDateField',
  components: {
    WorkTimePicker,
  },
  mixins: [isRequired, pendingAble],
  props: {
    range: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    isClearable: { type: Boolean, default: true },
    startPicker: {
      type: String,
      validator: (value) => ['YEAR', 'MONTH', 'DATE'].includes(value),
      default: undefined,
    },
    month: Boolean,
    includeMonth: { type: Boolean, default: false },
    dateTimeMode: { type: Boolean, default: false },
    min: String,
    max: String,
    hideYear: { type: Boolean, default: false },
    formatWithoutYear: { type: Boolean, default: false },
    scrollable: { type: Boolean, default: true },
    allowedDates: { type: Function, default: () => () => true },
    events: { type: Array, default: () => [] },
    eventColor: { type: Function, default: () => () => '' },
    ...VTextField.options.props,
  },
  data() {
    return {
      internalValues: [],
      dateModel: undefined, // input from vuetify date picker
      menu: false,
    };
  },
  setup() {
    const { registerTimeout } = useTimerManager();
    return {
      registerTimeout,
    };
  },
  computed: {
    formattedText: {
      get() {
        let start = this.internalValues?.[0];
        let end = this.internalValues?.[1] ?? start;

        if (start === undefined)
          return undefined;

        if (this.hideYear) {
          start = start?.split('-')?.[1];
          end = end?.split('-')?.[1];
        }

        if (this.range)
          return (`${dateService.formatISOString(start, this.month, this.dateTimeMode)} - ${dateService.formatISOString(end, this.month, this.dateTimeMode)}`);

        if (this.hideYear)
          return start;

        if (this.formatWithoutYear)
          return dateService.formatISOStringWithoutYear(start, this.month, this.dateTimeMode);

        return dateService.formatISOString(start, this.month, this.dateTimeMode);
      },
      set(newValue) {
        if (newValue === undefined || newValue === null) {
          this.internalValues = [];
          this.emitInput();
          this.dateModel = undefined;
          return;
        }

        this.internalValues = dateService.parseRange(
          newValue,
          this.month,
          this.dateTimeMode,
          this.includeMonth
        );
        this.emitInput();
        this.$refs.textField.lazyValue = this.formattedText;
      },
    },
    internalTimeValueFrom: {
      get() {
        if (this.internalValues == undefined)
          return undefined;

        return this.internalValues?.[0]?.split(' ')?.[1] ?? DEFAULT_TIME_OF_DAY;
      },
      set(val) {
        if (val === undefined || val.includes('NaN') || this.internalValues == undefined)
          return;

        const date = (this.range ? this.dateModel?.[0] : this.dateModel)
          ?? '2021-01-01';

        const newVals = [`${date} ${val}`];
        if (this.internalValues?.[1] !== undefined)
          newVals.push(this.internalValues[1]);

        this.internalValues = newVals;
        this.emitInput();
      }
    },
    internalTimeValueTo: {
      get() {
        if (this.internalValues == undefined || !this.range)
          return undefined;

        return this.internalValues[1]?.split(' ')?.[1] ?? '23:59';
      },
      set(val) {
        if (val === undefined || val.includes('NaN') || this.internalValues == undefined || !this.range)
          return;

        const date = (this.range ? (this.dateModel?.[1] ?? this.dateModel?.[0]) : this.dateModel)
          ?? '2021-01-01';

        this.internalValues = [this.internalValues?.[0], `${date} ${val}`];
        this.emitInput();
      }
    }
  },
  watch: {
    value(newVal, oldVal) {
      if (newVal === undefined) {
        this.internalValues = [];
        return;
      }

      this.update(newVal);
    },
    menu(val) {
      if (val && this.startPicker)
        this.registerTimeout(() => (this.$refs.picker.activePicker = this.startPicker));
    },
    dateTimeMode() {
      this.update(this.value);
    },
  },
  methods: {
    handleDatePickerInput(input) {
      this.updateDates(input);
      this.emitInput();
    },
    updateDates(input) {
      if (input == undefined)
        return;

      if (this.range) { // input is an array
        if (input.length == 0)
          return;

        let [start, end] = input;
        //If the start of the range has been selected after the end, the endDate is currently at index 0 and the startDate at index 1: switch elements
        if (start !== undefined && end !== undefined && start > end) {
          const tmp = start;
          start = end;
          end = tmp;
        }

        if (start === undefined || start.includes('NaN') || end?.includes('NaN'))
          return;

        //The day has to be added manually, if set by using the date picker of type 'month'
        if (start.length == 7 && this.month === true)
          start = `${start}-${dateService.getDayOfMonthPicker(start, this.includeMonth)}`;

        if (end?.length == 7 && this.month === true)
          end = `${end}-${dateService.getDayOfMonthPicker(end, this.includeMonth)}`;

        if (this.dateTimeMode) {
          if (end === undefined)
            end = start;

          start = `${start} ${this.internalTimeValueFrom ?? DEFAULT_TIME_OF_DAY}`;
          end = `${end} ${this.internalTimeValueTo ?? '23:59'}`;
        }

        this.internalValues = [start];
        if (end !== undefined)
          this.internalValues.push(end);
      } else { // input is a scalar
        if (input.includes('NaN'))
          return;

        let newVal = input;
        //The day has to be added manually, if set by using the date picker of type 'month'
        if (newVal.length == 7 && this.month === true)
          newVal = `${newVal}-${dateService.getDayOfMonthPicker(newVal, this.includeMonth)}`;

        if (this.dateTimeMode)
          newVal = `${newVal} ${this.internalTimeValueFrom ?? DEFAULT_TIME_OF_DAY}`;

        this.internalValues = [newVal];
      }
    },
    emitInput() {
      if (this.internalValues.length == 0)
        return;

      if (this.range) {
        this.$emit('input', this.internalValues);
        return;
      }

      this.$emit('input', this.internalValues[0]);
    },
    formatDateToMonthPicker(date) {
      if (REGEX.MONTH_YEAR.test(date) || !date) return date;
      return date.substring(0, 7)
    },
    focus() {
      this.$refs.textField.focus();
    },
    update(value) {
      if (value === undefined)
        return;

      if (this.range) {
        const start = value[0];
        const end = value[1];

        if (start.split(' ')?.[0] === end.split(' ')?.[0])
          this.dateModel = [start.split(' ')[0]];
        else
          this.dateModel = value.map(date => date.split(' ')[0]);

        if (this.dateTimeMode) {
          this.internalTimeValueFrom = start.split(' ')[1];

          if (value.length > 1)
            this.internalTimeValueTo = end.split(' ')[1];
        }
      } else {
        this.dateModel = value ? value.split(' ')[0] : value;
        if (this.dateTimeMode) {
          this.internalTimeValueFrom = value.split(' ')[1];
        }
      }
      this.updateDates(this.dateModel);
    },
    setFormattedDateStringAndParseDateToIso(val) {
      this.formattedText = val;
      this.dateModel = val != undefined ? dateService.parseToISO(val) : val;
    },
  },
  created() {
    this.update(this.value);
    this.emitInput();
  },
};
</script>

<style lang="scss">
.w-date-time-field-wrapper .v-picker {
  box-shadow: none;
}

.w-date-field {
  & .v-input__slot {
    background: $input-bg-color !important;
  }

  & .v-input__icon--append {
    &>button {
      color: $icon-color;
    }
  }

  & .v-text-field__slot {
    min-width: 5rem;
  }

  & legend {
    transition: none;
  }

  &__time {
    background-color: $cards-bg-color
  }
}

.v-date-picker__hide-header .v-date-picker-header {
  display: none;
}
</style>
